import { useRef, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationManager } from 'Components/Notifications';
import { searchAssetProfileEvents, createAssetProfileEvent, updateAssetProfileEvent, deleteAssetProfileEvent } from '../redux/slice';
import {
    SLIDE_KEYS_EXPORT as SLIDE_KEYS,
    parseSeverityParameters,
    getDefaultBooleanSeverity,
    slidesEqual,
    extractSlidesFromPoints,
    mphToKmh,
    kmhToMph,
    convertSlides,
} from '../utils/severityUtils';

export const useHarshBrake = (selectedEvent, onUnsavedChangesChange) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const { user: { isKph } } = useSelector((state) => state.securityRedux || { user: {} });
    
    const [isOpenPanelEdit, setIsOpenPanelEdit] = useState(false);
    const [tempSlides, setTempSlides] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const autoAppliedDefaultSeverityRef = useRef(false);
    const booleanDefaultSeverityRef = useRef(50);
    const booleanWasUnconfiguredRef = useRef(false);
    const baselineRef = useRef(null);
    const [formState, setFormState] = useState({
        status: false,
        coachable: false,
        severityValue: 50,
        radioValue: 'Absolute',
        slides: { start: 0, low: 0, medium: 0, high: 0, critical: 0 },
    });

    const severityParams = useMemo(() => parseSeverityParameters(selectedEvent?.severityParameters), [selectedEvent]);
    const severityType = severityParams.typeControl || 'Boolean';

    const SEVERITY_LABELS = useMemo(() => ({
        0: messages['driverPerformanceConfig_riskEvents_start'] || 'Start',
        25: messages['driverPerformanceConfig_riskEvents_low'] || 'Low',
        50: messages['driverPerformanceConfig_riskEvents_medium'] || 'Medium',
        75: messages['driverPerformanceConfig_riskEvents_high'] || 'High',
        100: messages['driverPerformanceConfig_riskEvents_critical'] || 'Critical',
    }), [messages]);

    const availableMethods = useMemo(() => {
        const relative = severityParams.typeMethod?.Relative;
        const absolute = severityParams.typeMethod?.Absolute;
        const hasRelative = !!(relative?.points || relative?.values);
        const hasAbsolute = !!(absolute?.points || absolute?.values);
        return { hasRelative, hasAbsolute, hasBoth: hasRelative && hasAbsolute };
    }, [severityParams]);

    const getSlidesForMethod = (method) => {
        const methodConfig = severityParams.typeMethod?.[method];
        if (!methodConfig) return null;
        
        if (Array.isArray(methodConfig.points) && methodConfig.points.length > 0) {
            return extractSlidesFromPoints(methodConfig.points);
        }
        
        if (Array.isArray(methodConfig.values) && methodConfig.values.length > 0) {
            const toNum = (v) => Number(typeof v === 'string' ? Number.parseFloat(v) : v);
            return {
                start: toNum(methodConfig.values[0]) || 0,
                low: toNum(methodConfig.values[1]) || 0,
                medium: toNum(methodConfig.values[2]) || 0,
                high: toNum(methodConfig.values[3]) || 0,
                critical: toNum(methodConfig.values[4]) || 0,
            };
        }
        return null;
    };

    const getInitialState = () => {
        let slides = { start: 0, low: 0, medium: 0, high: 0, critical: 0 };
        let radioValue = 'Absolute';
        let severityValue = 50;

        if (severityType === 'Non-Boolean') {
            radioValue = severityParams.selectedMethod || severityParams.defaultMethod || (availableMethods.hasAbsolute ? 'Absolute' : 'Relative');
            if (!severityParams.typeMethod?.[radioValue]) {
                radioValue = availableMethods.hasAbsolute ? 'Absolute' : 'Relative';
            }
            const methodSlides = getSlidesForMethod(radioValue);
            if (methodSlides) {
                slides = isKph && radioValue === 'Absolute' ? convertSlides(methodSlides, mphToKmh) : methodSlides;
            }
        } else {
            const defaultSeverity = getDefaultBooleanSeverity(selectedEvent, severityParams);
            const wasInactive = !selectedEvent.status;
            const severityIsZeroOrMissing = !severityParams.severity;
            
            booleanDefaultSeverityRef.current = defaultSeverity;
            booleanWasUnconfiguredRef.current = wasInactive && severityIsZeroOrMissing;
            severityValue = wasInactive && severityIsZeroOrMissing ? defaultSeverity : (severityParams.severity ?? defaultSeverity);
        }

        return { slides, radioValue, severityValue };
    };

    useEffect(() => {
        if (selectedEvent) {
            autoAppliedDefaultSeverityRef.current = false;
            const { slides, radioValue, severityValue } = getInitialState();
            const nextBaseline = {
                status: !!selectedEvent.status,
                coachable: !!selectedEvent.coachable,
                severityValue,
                radioValue,
                slides,
            };
            setTempSlides(null);
            setIsOpenPanelEdit(false);
            baselineRef.current = nextBaseline;
            setFormState(nextBaseline);
        }
    }, [selectedEvent, severityParams, availableMethods, isKph, severityType]);

    const hasUnsavedChanges = useMemo(() => {
        if (!selectedEvent || !baselineRef.current) return false;
        const baseline = baselineRef.current;
        if (Boolean(baseline.status) !== Boolean(formState.status)) return true;
        if (Boolean(baseline.coachable) !== Boolean(formState.coachable)) return true;
        if (severityType === 'Boolean') {
            return Number(baseline.severityValue) !== Number(formState.severityValue);
        }
        if (String(baseline.radioValue) !== String(formState.radioValue)) return true;
        return !slidesEqual(baseline.slides, tempSlides || formState.slides);
    }, [selectedEvent, formState, tempSlides, severityType]);

    useEffect(() => {
        onUnsavedChangesChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onUnsavedChangesChange]);

    const handleModeChange = (newMode) => {
        const methodSlides = getSlidesForMethod(newMode);
        if (!methodSlides) return;
        const newSlides = isKph && newMode === 'Absolute' ? convertSlides(methodSlides, mphToKmh) : methodSlides;
        setFormState(prev => ({ ...prev, radioValue: newMode, slides: newSlides }));
        setTempSlides(null);
        setIsOpenPanelEdit(false);
    };

    const sliderMax = formState.radioValue === 'Relative' ? 100 : (isKph ? Math.round(mphToKmh(25)) : 25);
    const unit = formState.radioValue === 'Relative' ? '%' : (isKph ? 'km/h' : 'mph');
    const stepSize = formState.radioValue === 'Relative' ? 20 : (isKph ? Math.round(mphToKmh(5)) : 5);
    const isActive = !!formState.status;
    const currentSlides = tempSlides || formState.slides;

    const handleChangeSlides = (key, value) => {
        setTempSlides(prev => {
            const currentSlides = prev || formState.slides;
            const index = SLIDE_KEYS.indexOf(key);
            const minValue = index > 0 ? currentSlides[SLIDE_KEYS[index - 1]] : 0;
            const maxValue = index < SLIDE_KEYS.length - 1 ? currentSlides[SLIDE_KEYS[index + 1]] : sliderMax;
            return { ...currentSlides, [key]: Math.max(minValue, Math.min(maxValue, value)) };
        });
    };

    const handleChangeFormState = (key, value) => {
        if (key === 'status' && value && severityType === 'Boolean' && booleanWasUnconfiguredRef.current && !autoAppliedDefaultSeverityRef.current) {
            autoAppliedDefaultSeverityRef.current = true;
            setFormState(prev => ({ ...prev, status: value, severityValue: booleanDefaultSeverityRef.current }));
            return;
        }
        setFormState(prev => ({ ...prev, [key]: value }));
    };

    const chartData = useMemo(() => ({
        datasets: [{
            label: selectedEvent?.eventName || 'Event',
            data: [
                { x: currentSlides.start, y: 0 },
                { x: currentSlides.low, y: 25 },
                { x: currentSlides.medium, y: 50 },
                { x: currentSlides.high, y: 75 },
                { x: currentSlides.critical, y: 100 },
            ],
            borderColor: '#4a90d9',
            backgroundColor: '#fff',
            pointBackgroundColor: '#fff',
            pointBorderColor: '#000',
            pointBorderWidth: 2,
            pointRadius: 6,
            fill: false,
            tension: 0,
        }]
    }), [currentSlides, selectedEvent]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.parsed.x} ${unit} - ${SEVERITY_LABELS[context.parsed.y] || context.parsed.y}`,
                },
            },
        },
        scales: {
            y: {
                min: 0,
                max: 100,
                ticks: { stepSize: 25 },
                grid: { borderDash: [5, 5], color: '#e0e0e0' },
            },
            y2: {
                position: 'right',
                min: 0,
                max: 100,
                ticks: {
                    stepSize: 25,
                    callback:'',
                },
                grid: { drawOnChartArea: false },
            },
            x: {
                type: 'linear',
                min: 0,
                max: sliderMax,
                ticks: {
                    stepSize: stepSize,
                    callback: (value) => `${value} ${unit}`,
                },
                grid: { display: false },
            },
        },
    }), [sliderMax, unit, stepSize]);

    const buildSeverityParameters = () => {
        const eventName = selectedEvent?.eventName || null;
        
        if (severityType === 'Boolean') {
            return JSON.stringify({ 
                typeControl: 'Boolean', 
                severity: formState.severityValue ?? 0,
                ...(eventName && { eventName })
            });
        }

        const base = parseSeverityParameters(selectedEvent.severityParameters);
        let slidesToSave = formState.slides;
        if (isKph && formState.radioValue === 'Absolute') {
            slidesToSave = convertSlides(formState.slides, kmhToMph);
        }

        const points = SLIDE_KEYS.map((key, i) => ({ x: slidesToSave[key], y: i * 25, label: ['Start', 'Low', 'Medium', 'High', 'Critical'][i] }));

        return JSON.stringify({
            ...base,
            typeControl: 'Non-Boolean',
            defaultMethod: base.defaultMethod || severityParams.defaultMethod || formState.radioValue,
            selectedMethod: formState.radioValue,
            ...(eventName && { eventName }),
            typeMethod: {
                ...(base.typeMethod || {}),
                [formState.radioValue]: {
                    ...((base.typeMethod || {})[formState.radioValue] || {}),
                    points,
                },
            },
        });
    };

    const hasSeverityChanged = () => {
        const prevParams = parseSeverityParameters(selectedEvent.severityParameters);
        
        if (severityType === 'Boolean') {
            const prevSeverity = prevParams.severity ?? prevParams.defaultSeverity ?? 0;
            return Number(prevSeverity) !== Number(formState.severityValue ?? 0);
        }

        const prevMethod = prevParams.selectedMethod || prevParams.defaultMethod || severityParams.defaultMethod || formState.radioValue;
        if (prevMethod !== formState.radioValue) return true;

        const prevConfig = prevParams?.typeMethod?.[formState.radioValue] || {};
        const prevPoints = prevConfig.points;
        let prevValues = [];
        
        if (Array.isArray(prevPoints)) {
            prevValues = extractSlidesFromPoints(prevPoints);
            if (isKph && formState.radioValue === 'Absolute') {
                prevValues = convertSlides(prevValues, mphToKmh);
            }
            prevValues = SLIDE_KEYS.map(k => prevValues[k]);
        } else if (Array.isArray(prevConfig.values)) {
            prevValues = prevConfig.values.map(v => Number(typeof v === 'string' ? Number.parseFloat(v) : v));
            if (isKph && formState.radioValue === 'Absolute') {
                prevValues = prevValues.map(v => mphToKmh(v));
            }
        }

        const nextValues = SLIDE_KEYS.map(k => Number(formState.slides[k]));
        return prevValues.length !== nextValues.length || prevValues.some((v, i) => Math.abs(Number(v) - nextValues[i]) > 0.01);
    };

    const handleSave = async () => {
        if (!selectedEvent?.profileId || !selectedEvent?.eventId || isSaving) return;

        const prevActive = !!selectedEvent.status;
        const nextActive = !!formState.status;
        const coachableChanged = !!selectedEvent.coachable !== !!formState.coachable;
        const severityChanged = hasSeverityChanged();

        if (prevActive === nextActive && !coachableChanged && !severityChanged) return;

        setIsSaving(true);
        try {
            const payload = {
                profileId: selectedEvent.profileId,
                eventId: selectedEvent.eventId,
                status: nextActive,
                coachable: !!formState.coachable,
                severityParameters: buildSeverityParameters(),
            };

            if (prevActive && !nextActive) {
                await dispatch(deleteAssetProfileEvent({ profileId: payload.profileId, eventId: payload.eventId })).unwrap();
                NotificationManager.success(messages['success'] || 'Success', messages['driverPerformanceConfig_riskEvents_eventDeletedSuccessfully'], 3000);
            } else if (!prevActive && nextActive) {
                await dispatch(createAssetProfileEvent(payload)).unwrap();
                NotificationManager.success(messages['success'] || 'Success', messages['driverPerformanceConfig_riskEvents_eventCreatedSuccessfully'], 3000);
            } else if (coachableChanged || severityChanged) {
                await dispatch(updateAssetProfileEvent(payload)).unwrap();
                NotificationManager.success(messages['success'] || 'Success', messages['driverPerformanceConfig_riskEvents_eventUpdatedSuccessfully'], 3000);
            }

            baselineRef.current = {
                status: !!formState.status,
                coachable: !!formState.coachable,
                severityValue: formState.severityValue,
                radioValue: formState.radioValue,
                slides: { ...formState.slides },
            };
            onUnsavedChangesChange?.(false);
            dispatch(searchAssetProfileEvents({ profileId: payload.profileId, loadMore: false }));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setTempSlides(null);
        setIsOpenPanelEdit(false);
    };

    const handleSaveEdit = () => {
        setFormState(prev => ({ ...prev, slides: tempSlides }));
        setTempSlides(null);
        setIsOpenPanelEdit(false);
    };

    const handleEditClick = () => {
        setTempSlides({ ...formState.slides });
        setIsOpenPanelEdit(true);
    };

    return {
        formState,
        isOpenPanelEdit,
        isSaving,
        isActive,
        currentSlides,
        severityType,
        availableMethods,
        sliderMax,
        unit,
        stepSize,
        chartData,
        chartOptions,
        disabledStyle: !isActive ? { opacity: 0.5, pointerEvents: 'none' } : undefined,
        handleChangeFormState,
        handleChangeSlides,
        handleModeChange,
        handleSave,
        handleCancelEdit,
        handleSaveEdit,
        handleEditClick,
    };
};
